import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { isAuthenticated, getCurrentUser, logout } from '../services/authService';
import api from '../services/api';
import {
    Layout as AntLayout, Menu as AntMenu, Card, Row, Col, Button, Spin, message, Alert, Tabs, Empty,
    Modal, Form, Input, Tag, Space, Statistic, Typography, Grid, Tooltip, Divider, Descriptions,
    DatePicker, Select, Popconfirm,
    Pagination
} from 'antd';
import {
    ShopOutlined, BarChartOutlined, CreditCardOutlined, UserOutlined, LogoutOutlined,
    PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, ExclamationCircleOutlined,
    LockOutlined, StarFilled, LikeOutlined, SettingOutlined, DollarCircleOutlined, SyncOutlined,
    ReloadOutlined, StopOutlined, PlayCircleOutlined, CheckCircleOutlined, CloseCircleOutlined,
    MailOutlined, InfoCircleOutlined
} from '@ant-design/icons';
import './DashboardPage.css';
import ShopStatistics from '../components/ShopStatistics';
import PricingPlans from '../components/PricingPlans';
import { getPlans } from '../services/paymentService';
import { loadStripe } from '@stripe/stripe-js';

const { confirm } = Modal;
const { Header, Content, Sider, Footer } = AntLayout;
const { Title, Text, Paragraph, Link: AntLink } = Typography;
const { useBreakpoint } = Grid;
const { Option } = Select;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

// Helper function to get next plan details
const getNextPlan = (plans, currentPlanId) => {
    const currentIndex = plans.findIndex(p => p.id === currentPlanId);
    if (currentIndex === -1 || currentIndex === plans.length - 1) {
        return null; // No next plan or current plan not found
    }
    return plans[currentIndex + 1];
};

// Define getPlanTagColor
const getPlanTagColor = (planId) => {
    switch (planId) {
        case 'premium': return 'purple';
        case 'professional': return 'gold';
        case 'basic':
        default: return 'blue';
    }
};

// Add stripePromise initialization if not already present globally
let stripePromise; // Declare it
try {
    const stripeKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
    if (stripeKey) {
        stripePromise = loadStripe(stripeKey);
    } else {
        console.error('Stripe Public Key not found in environment variables for Dashboard');
    }
} catch (error) {
    console.error('Error initializing Stripe for Dashboard:', error);
}

// Helper für den Status-Text der Shop-Aufforderung
const getShopActionText = (status, isActive) => {
    if (!isActive && status === 'pending') return 'Zahlung abschließen, um Shop zu aktivieren.';
    if (!isActive && status === 'inactive' && status !== 'cancelled') return 'Wähle einen Tarif, um diesen Shop zu aktivieren.';
    if (status === 'past_due' || status === 'unpaid') return 'Zahlungsproblem. Bitte Tarif aktualisieren.';
    if (status === 'incomplete') return 'Zahlung unvollständig. Bitte abschließen.';
    if (status === 'cancelled') return 'Abonnement gekündigt. Wähle einen neuen Tarif, um den Shop wieder zu aktivieren.';
    if (!isActive) return 'Shop ist inaktiv. Wähle einen Tarif.'; // Fallback
    return ''; // Kein spezieller Text nötig
};

// Helper für den Typ der Alert-Box
const getShopActionType = (status, isActive) => {
    if (!isActive && status === 'pending') return 'warning';
    if (status === 'past_due' || status === 'unpaid' || status === 'incomplete') return 'error';
    if (status === 'cancelled') return 'warning';
    if (!isActive) return 'info';
    return 'info'; // Default
};

// Helper für Subscription Status Tags (Beispiel, falls noch nicht vorhanden)
const getSubscriptionTagColor = (status) => {
    switch (status) {
        case 'active': return 'success';
        case 'trialing': return 'processing';
        case 'pending':
        case 'past_due':
        case 'unpaid': return 'error';
        case 'incomplete':
        case 'cancelled': return 'warning';
        case 'inactive':
        default: return 'default';
    }
};

const getSubscriptionStatusText = (status) => {
    switch (status) {
        case 'active': return 'Aktiv';
        case 'trialing': return 'Testphase';
        case 'pending': return 'Zahlung ausstehend';
        case 'past_due': return 'Überfällig';
        case 'unpaid': return 'Unbezahlt';
        case 'incomplete': return 'Unvollständig';
        case 'cancelled': return 'Gekündigt';
        case 'inactive': return 'Inaktiv';
        default: return status || 'Unbekannt';
    }
};

const DashboardPage = () => {
    const [user, setUser] = useState(null);
    const [shops, setShops] = useState([]);
    const [activeTabKey, setActiveTabKey] = useState('shops');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [shopToDelete, setShopToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [changePasswordForm] = Form.useForm();
    const [isChangingPassword, setIsChangingPassword] = useState(false);
    const [selectedShopIdForStats, setSelectedShopIdForStats] = useState(null);
    const [currentShopStats, setCurrentShopStats] = useState(null);
    const [loadingCurrentShopStats, setLoadingCurrentShopStats] = useState(false);
    const [currentShopStatsError, setCurrentShopStatsError] = useState(null);
    const [plans, setPlans] = useState([]);
    const [isEditingProfile, setIsEditingProfile] = useState(false);
    const [editProfileForm] = Form.useForm();
    const [isDeletingAccount, setIsDeletingAccount] = useState(false);
    const [isDeletingShop, setIsDeletingShop] = useState(false);
    const [shopBeingDeleted, setShopBeingDeleted] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    const screens = Grid.useBreakpoint(); // Korrekter Aufruf von useBreakpoint
    const messageShownRef = useRef(false); // Ref, um zu tracken, ob die Nachricht gezeigt wurde

    // *** NEUE Paginierungs-States ***
    const [pagination, setPagination] = useState({ currentPage: 1, limit: 10, totalItems: 0 });
    const [loadingPage, setLoadingPage] = useState(false); // Separater Ladezustand für Seitenwechsel

    useEffect(() => {
        const successMsg = location.state?.successMessage;
        console.log('[Dashboard Success Effect] Running. successMsg:', successMsg, 'Ref:', messageShownRef.current);

        if (successMsg) {
            // Fall 1: Nachricht ist da
            if (!messageShownRef.current) {
                // Fall 1a: Nachricht ist neu (Ref ist false)
                console.log('[Dashboard Success Effect] Showing message:', successMsg);
                message.success(successMsg);
                messageShownRef.current = true; // Markieren, dass für *diese* Nachricht angezeigt
                console.log('[Dashboard Success Effect] Attempting to clear state...');
                // State sofort löschen
                navigate(location.pathname, { replace: true, state: {} }); 
            } else {
                 // Fall 1b: Nachricht ist noch da, aber Ref ist schon true (sollte nicht passieren, wenn State-Clearing klappt)
                 // -> Nachricht NICHT nochmal anzeigen
                 console.log('[Dashboard Success Effect] Message already shown for this state.');
                 // Sicherheitshalber State nochmal löschen, falls der vorherige Versuch fehlschlug
                 if (Object.keys(location.state).length > 0 && location.state.successMessage) {
                    console.log('[Dashboard Success Effect] Forcing state clear again.');
                    navigate(location.pathname, { replace: true, state: {} });
                 }
            }
        } else {
            // Fall 2: Keine Nachricht (mehr) im State
            // -> Ref nur zurücksetzen, wenn er vorher true war (d.h. eine Nachricht wurde angezeigt und der State ist jetzt leer)
            if (messageShownRef.current) {
                 console.log('[Dashboard Success Effect] No success message found, resetting messageShownRef.');
                 messageShownRef.current = false; 
            } else {
                // Fall 2b: Keine Nachricht und Ref war eh false -> alles ok.
                console.log('[Dashboard Success Effect] No success message, ref already false.');
            }
        }
        // Dependencies: location.state (um auf Änderungen zu reagieren), pathname und navigate
    }, [location.state, location.pathname, navigate]);

    // NEUER useEffect für die Cancel-Nachricht bei Registrierung
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const cancelled = params.get('registration_payment_cancelled');
        const shopId = params.get('shop_id'); // Shop-ID aus der URL holen

        if (cancelled === 'true' && shopId) {
            console.log(`Dashboard: Registrierungszahlung für Shop ${shopId} abgebrochen.`);
            // Finde den Namen des Shops (optional, für bessere Nachricht)
            const cancelledShop = shops.find(s => s.id === shopId);
            const shopName = cancelledShop ? `"${cancelledShop.name}"` : `Shop mit ID ${shopId}`;
            
            // Nachricht nur anzeigen, wenn sie nicht schon durch successMessage gezeigt wurde
            if (!messageShownRef.current) {
                message.warning(
                    `Die Zahlung für die Registrierung von ${shopName} wurde abgebrochen. ` +
                    `Der Shop ist noch nicht aktiv. Bitte schließen Sie die Zahlung ab.`,
                    7 // Dauer der Nachricht in Sekunden
                );
                messageShownRef.current = true; // Verhindert doppelte Nachrichten (auch von successMessage)
            
                // Clean the search params from the URL
                navigate(location.pathname, { replace: true, state: {} }); 
            } else {
                 console.log('Dashboard: Registrierungs-Abbruch-Nachricht übersprungen, da bereits eine Nachricht angezeigt wurde.');
            }
        } else if (cancelled !== 'true') {
            // Wenn kein Abbruch vorliegt, Ref zurücksetzen für nächsten pot. Abbruch
             messageShownRef.current = false;
        }
        // Dependencies: location.search (um auf Änderungen zu reagieren), navigate, shops (um Namen zu finden)
    }, [location.search, navigate, shops]); 

    useEffect(() => {
        const checkAuth = async () => {
            try {
                setLoading(true);
                setError(null);
                setPlans(getPlans());

                const authenticated = await isAuthenticated();
                if (authenticated) {
                    console.log('User authenticated');
                    const currentUser = await getCurrentUser();
                    setUser(currentUser);
                    await loadShopData(currentUser, 1, pagination.limit);
                } else {
                    console.log('No user found, redirecting to login');
                    navigate('/login', { state: { from: '/dashboard' } });
                }
            } catch (error) {
                console.error('Auth check error:', error);
                setError('Authentifizierungsfehler. Bitte versuchen Sie, sich erneut anzumelden.');
                message.error('Authentifizierungsfehler. Weiterleitung zum Login...');
                setTimeout(() => {
                    navigate('/login');
                }, 2000);
            } finally {
            }
        };

        checkAuth();
    }, [navigate]);

    useEffect(() => {
        if (user) {
            editProfileForm.setFieldsValue({
                name: user.name,
                email: user.email,
            });
        }
    }, [user, editProfileForm]); // Initialize profile form when user data is available

    const loadShopData = async (currentUser, page = 1, limit = pagination.limit) => {
        // Initiales Laden: setLoading, Seitenwechsel: setLoadingPage
        if (page === 1) {
             setLoading(true);
        } else {
            setLoadingPage(true);
        }
        setError(null);

        try {
            console.log(`Dashboard: Lade Shop-Daten für Seite ${page}, Limit ${limit}`);
            if (!currentUser || !currentUser.id) {
                throw new Error('Benutzer ist nicht authentifiziert');
            }

            let endpoint = '';
            // const params = { page, limit }; // Paginierungsparameter hinzufügen - Alte Methode

            if (currentUser.role === 'admin') {
                console.log('Dashboard: Admin detected. Lade alle Shops...');
                endpoint = '/admin/shops';
            } else {
                console.log('Dashboard: Lade Shops für normalen Benutzer...');
                endpoint = '/shops/me';
                // Hinweis: Wenn /shops/me auch paginiert werden soll, muss das Backend angepasst werden!
            }

            // *** START MODIFICATION: Query-String manuell bauen ***
            const queryString = `?page=${page}&limit=${limit}`;
            const urlWithParams = `${endpoint}${queryString}`;
            console.log(`Dashboard: Rufe URL auf: ${urlWithParams}`);
            const shopsResponse = await api.get(urlWithParams);
            // *** END MODIFICATION ***

            // const shopsResponse = await api.get(endpoint, { params }); // Alte Methode

            if (shopsResponse.success && Array.isArray(shopsResponse.data)) {
                console.log(`Dashboard: ${shopsResponse.count} von ${shopsResponse.total || 'unbekannt'} Shops geladen (Admin: ${currentUser.role === 'admin'})`);
                setShops(shopsResponse.data);

                // Paginierungs-State aktualisieren (nur wenn vom Backend geliefert)
                if (shopsResponse.pagination) {
                     setPagination({
                        currentPage: shopsResponse.pagination.currentPage,
                        totalPages: shopsResponse.pagination.totalPages,
                        totalItems: shopsResponse.pagination.totalItems,
                        limit: limit
                    });
                } else {
                    // Fallback für Endpunkte ohne Paginierung (wie /shops/me aktuell)
                    setPagination({
                        currentPage: 1,
                        totalPages: 1,
                        totalItems: shopsResponse.data.length,
                        limit: limit
                    });
                }

                // Auto-Select für Stats anpassen (nur wenn auf Seite 1 neue Daten geladen werden)
                if (page === 1) {
                    if (shopsResponse.data.length > 0 && !selectedShopIdForStats) {
                        setSelectedShopIdForStats(shopsResponse.data[0]._id); // Annahme: ID ist _id
                    } else if (shopsResponse.data.length === 0) {
                        setSelectedShopIdForStats(null);
                    }
                }

            } else {
                console.warn(`Dashboard: Keine Shops gefunden oder API-Fehler (Admin: ${currentUser.role === 'admin'}):`, shopsResponse.message);
                setShops([]);
                setSelectedShopIdForStats(null);
                 setPagination({ currentPage: 1, totalPages: 1, totalItems: 0, limit: limit }); // Reset Pagination
            }

        } catch (err) {
            console.error('Dashboard: Fehler beim Laden der Shop-Daten:', err);
            setError('Fehler beim Laden Ihrer Shop-Daten. Bitte versuchen Sie es später erneut oder laden Sie die Seite neu.');
            message.error('Fehler beim Laden der Shop-Daten.');
            setShops([]);
            setSelectedShopIdForStats(null);
            setPagination({ currentPage: 1, totalPages: 1, totalItems: 0, limit: limit }); // Reset Pagination
        } finally {
             setLoading(false);
             setLoadingPage(false);
        }
    };

    const loadStatsForShop = useCallback(async (shopId) => {
        if (!shopId) {
            console.log('[loadStatsForShop] Keine Shop ID angegeben.');
            setCurrentShopStats(null);
            setCurrentShopStatsError(null);
            return;
        }
        console.log(`[loadStatsForShop] Lade Statistiken für Shop ID: ${shopId}...`);
        setLoadingCurrentShopStats(true);
        setCurrentShopStatsError(null);
        setCurrentShopStats(null);

        try {
            console.log(`[loadStatsForShop] Sende GET /shops/${shopId}/stats...`);
            const response = await api.get(`/shops/${shopId}/stats`);
            console.log(`[loadStatsForShop] Antwort für ${shopId} erhalten:`, response);
            if (response.success && response.data) {
                console.log(`[loadStatsForShop] Setze currentShopStats für ${shopId}:`, response.data);
                setCurrentShopStats(response.data);
            } else {
                throw new Error(response.message || 'Statistiken für diesen Shop konnten nicht geladen werden.');
            }
        } catch (err) {
            console.error(`[loadStatsForShop] Fehler beim Laden der Statistiken für Shop ${shopId}:`, err);
            setCurrentShopStatsError(`Fehler beim Laden der Statistiken für diesen Shop. (${err.message})`);
            message.error(`Fehler beim Laden der Statistiken für den Shop.`);
        } finally {
            setLoadingCurrentShopStats(false);
        }
    }, []);

    useEffect(() => {
        if (selectedShopIdForStats) {
            loadStatsForShop(selectedShopIdForStats);
        }
    }, [selectedShopIdForStats, loadStatsForShop]);

    const handleTabChange = (key) => {
        setActiveTabKey(key);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString('de-DE', {
                day: '2-digit', month: '2-digit', year: 'numeric',
            });
        } catch (e) {
            return 'Ungültiges Datum';
        }
    };

    const handleLogout = async () => {
        confirm({
            title: 'Möchten Sie sich wirklich abmelden?',
            icon: <LogoutOutlined />,
            content: 'Sie werden zur Startseite weitergeleitet.',
            okText: 'Abmelden',
            okType: 'danger',
            cancelText: 'Abbrechen',
            onOk: async () => {
        try {
            await logout();
                    message.success('Erfolgreich abgemeldet.');
            navigate('/');
        } catch (error) {
            console.error('Logout error:', error);
                    message.error('Abmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.');
                }
            },
        });
    };

    const confirmDeleteShop = (shop) => {
        setShopToDelete(shop); // Temporär speichern für den Handler

        if (shop.isActive && shop.stripeSubscriptionId) {
            // Fall 1: Aktiver Shop mit Abo -> Kündigen UND Löschen
            confirm({
                title: `Shop "${shop.name}" löschen & Abo kündigen?`,
                icon: <ExclamationCircleOutlined />,
                content: (
                    <div>
                        <p>Das zugehörige Stripe-Abonnement wird zum Ende des aktuellen Zeitraums gekündigt.</p>
                        <p>Der Shop wird danach endgültig gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.</p>
                    </div>
                ),
                okText: 'Ja, Abo kündigen und löschen',
                okType: 'danger',
                cancelText: 'Abbrechen',
                confirmLoading: isDeleting,
                onOk: async () => {
                    // Benutzerdefinierter Handler für Kündigen+Löschen
                    if (!shop) return; // Sicherheitshalber, shop kommt aus Closure
                    setIsDeleting(true);
                    const cancelKey = `cancel-${shop.id}`;
                    const deleteKey = `delete-${shop.id}`;
                    try {
                        // Schritt 1: Abo kündigen
                        message.loading({ content: `Kündige Abonnement für ${shop.name}...`, key: cancelKey });
                        const cancelResponse = await api.post('/payments/cancel-subscription', { shopId: shop.id });

                        if (!cancelResponse.success) {
                            throw new Error(cancelResponse.message || 'Abo-Kündigung fehlgeschlagen.');
                        }
                        message.success({ content: `Abonnement für ${shop.name} zur Kündigung vorgemerkt.`, key: cancelKey, duration: 3 });

                        // Schritt 2: Shop löschen
                        message.loading({ content: `Lösche Shop "${shop.name}"...`, key: deleteKey });
                        const deleteResponse = await api.delete(`/shops/${shop.id}`);

                        if (!deleteResponse.success) {
                            throw new Error(deleteResponse.message || 'Shop-Löschung fehlgeschlagen nach Abo-Kündigung.');
                        }

                        // UI aktualisieren
                        setShops(prevShops => prevShops.filter(s => s.id !== shop.id));
                        message.success({ content: `Shop "${shop.name}" wurde erfolgreich gelöscht.`, key: deleteKey, duration: 3 });

                    } catch (error) {
                        console.error('Fehler beim Kündigen/Löschen des Shops:', error);
                        message.error({ content: `Fehler: ${error.message || 'Unbekannter Fehler'}`, key: deleteKey, duration: 5 });
                        // Optional: Fehlermeldung für Kündigung entfernen, falls Löschung fehlschlägt
                        message.destroy(cancelKey);
                        setError('Fehler beim Löschen des Shops oder Kündigen des Abonnements.');
                    } finally {
                        setIsDeleting(false);
                        setShopToDelete(null);
                        // Stelle sicher, dass alle Ladeanzeigen entfernt werden
                        message.destroy(cancelKey);
                        message.destroy(deleteKey);
                    }
                },
                onCancel: () => setShopToDelete(null),
            });
        } else {
            // Fall 2: Inaktiver Shop oder ohne Abo -> Direkt löschen (alte Logik)
            confirm({
                title: `Möchten Sie den Shop "${shop.name}" wirklich löschen?`,
                icon: <ExclamationCircleOutlined />,
                content: 'Diese Aktion kann nicht rückgängig gemacht werden. Alle Daten dieses Shops gehen verloren.',
                okText: 'Ja, löschen',
                okType: 'danger',
                cancelText: 'Abbrechen',
                confirmLoading: isDeleting,
                // Ruft die bestehende handleDeleteShop auf, die nur api.delete macht
                onOk: handleDeleteShop, 
                onCancel: () => setShopToDelete(null),
            });
        }
    };

    const handleDeleteShop = async (shopId) => {
        setShopBeingDeleted(shopId); // Markieren, welcher Shop gelöscht wird (für Ladeanzeige)
        setIsDeletingShop(true);
        try {
            console.log(`Dashboard: Versuche Shop ${shopId} als Admin zu löschen...`);
            // Verwende den Admin-Endpunkt zum Löschen
            const response = await api.delete(`/admin/shops/${shopId}`);

            if (response.success) {
                message.success('Shop erfolgreich gelöscht!');
                // Shop aus dem State entfernen
                setShops(prevShops => prevShops.filter(shop => shop._id !== shopId)); // Annahme: ID ist _id
                 // Wenn der gelöschte Shop für Statistiken ausgewählt war, Auswahl zurücksetzen
                if (selectedShopIdForStats === shopId) {
                    setSelectedShopIdForStats(shops.length > 1 ? shops.find(s => s._id !== shopId)._id : null);
                }
            } else {
                console.error('Fehler beim Löschen des Shops:', response.message);
                message.error(response.message || 'Fehler beim Löschen des Shops.');
            }
        } catch (err) {
            console.error('Schwerer Fehler beim Löschen des Shops:', err);
            message.error('Ein Fehler ist aufgetreten. Der Shop konnte nicht gelöscht werden.');
        } finally {
            setIsDeletingShop(false);
            setShopBeingDeleted(null);
        }
    };

    const handleChangePassword = async (values) => {
        setIsChangingPassword(true);
        message.loading({ content: 'Ändere Passwort...', key: 'changePassword' });
        try {
            // API-Aufruf zum Ändern des Passworts
            // Annahme: Endpunkt ist /auth/change-password und erwartet { currentPassword, newPassword }
            // Ändere POST zu PUT entsprechend der Routendefinition im Backend
            const response = await api.put('/auth/change-password', {
                currentPassword: values.currentPassword,
                newPassword: values.newPassword,
            });

            if (response.success) {
                message.success({ content: 'Passwort erfolgreich geändert.', key: 'changePassword', duration: 3 });
                changePasswordForm.resetFields();
            } else {
                // Nutze die Fehlermeldung vom Backend, falls vorhanden
                throw new Error(response.message || 'Passwortänderung fehlgeschlagen.');
            }

        } catch (error) {
            console.error("Fehler beim Ändern des Passworts:", error);
            // Detailliertere Fehlermeldung anzeigen
            const errorMessage = error.response?.data?.message || error.message || 'Passwortänderung fehlgeschlagen. Bitte versuchen Sie es erneut.';
            message.error({ content: `Fehler: ${errorMessage}`, key: 'changePassword', duration: 5 });
        } finally {
            setIsChangingPassword(false);
        }
    };

    // Handler für Abo-Kündigung
    const handleCancelSubscription = async (shop) => {
        if (!shop || !shop.id || !shop.stripeSubscriptionId) {
            message.error('Kündigung nicht möglich: Fehlende Shop- oder Abonnement-Informationen.');
            return;
        }

        confirm({
            title: `Abonnement für "${shop.name}" kündigen?`,
            icon: <ExclamationCircleOutlined />,
            content: 'Das Abonnement läuft bis zum Ende des aktuellen Abrechnungszeitraums weiter.',
            okText: 'Ja, kündigen',
            okType: 'danger',
            cancelText: 'Abbrechen',
            // Füge einen Ladezustand hinzu, falls erforderlich
            // confirmLoading: isCancelling, // (Neuen State hinzufügen)
            onOk: async () => {
                // const key = `cancel-${shop.id}`; // Eindeutiger Key für message
                message.loading({ content: `Kündige Abonnement für ${shop.name}...`, key: `cancel-${shop.id}` });
                try {
                    const response = await api.post('/payments/cancel-subscription', { shopId: shop.id });

                    if (response.success) {
                        message.success({ content: `Abonnement für ${shop.name} wurde gekündigt.`, key: `cancel-${shop.id}`, duration: 4 });
                        // Aktualisiere den Shop-Status im Frontend
                        setShops(prevShops => prevShops.map(s =>
                            s.id === shop.id ? { ...s, subscriptionStatus: 'cancelled' } : s
                        ));
                    } else {
                        throw new Error(response.message || 'Kündigung fehlgeschlagen.');
                    }
                } catch (error) {
                    console.error('Fehler beim Kündigen des Abonnements:', error);
                    const errorMessage = error.response?.data?.message || error.message || 'Unbekannter Fehler.';
                    message.error({ content: `Fehler: ${errorMessage}`, key: `cancel-${shop.id}`, duration: 5 });
                }
            },
        });
    };

    const handleCancelSubscriptionImmediately = async (shop) => {
        if (!shop || !shop.id || !shop.stripeSubscriptionId || shop.subscriptionStatus !== 'cancelled') {
            message.error('Shop kann nicht sofort beendet werden oder die Bedingungen sind nicht erfüllt.');
            return;
        }

        confirm({
            title: `Abonnement für "${shop.name}" sofort beenden?`,
            icon: <ExclamationCircleOutlined />,
            content: 'Der Shop wird sofort deaktiviert und das Abonnement bei Stripe gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.',
            okText: 'Sofort beenden',
            okType: 'danger',
            cancelText: 'Abbrechen',
            onOk: async () => {
                try {
                    setIsDeleting(true); // Reuse deleting state or add a new one if needed
                    console.log(`[Immediate Cancel Frontend] Calling API for shop: ${shop.id}`);
                    const response = await api.post('/payments/cancel-immediately', { shopId: shop.id });

                    if (response.success) {
                        message.success(response.message || 'Abonnement erfolgreich sofort beendet.');
                        // Reload data to reflect changes
                        await loadShopData(user, 1, pagination.limit);
                    } else {
                        message.error(response.message || 'Fehler beim sofortigen Beenden des Abonnements.');
                    }
                } catch (error) {
                    console.error('Fehler beim sofortigen Kündigen:', error);
                    message.error('Ein unerwarteter Fehler ist aufgetreten.');
                } finally {
                    setIsDeleting(false);
                }
            },
        });
    };

    const handlePlanSelectInDashboard = (selectedPlan) => {
        console.log("Plan im Dashboard betrachtet:", selectedPlan);
        // Optional: Navigiere zu einer Info-Seite oder zeige Modal
        // navigate(`/pricing`); // Beispiel
    };

    const handleActivateSubscription = async (shop) => {
        // Log the received shop object for debugging
        console.log("Attempting to activate subscription for shop:", shop);

        if (!shop || !shop.id || !shop.subscriptionPlan || !shop.subscriptionInterval) {
            console.error("Fehlende Shop-Daten für Aktivierung:", shop);
            message.error("Fehlende Shop-Informationen, um die Zahlung zu starten.");
            return;
        }

        message.loading({ content: 'Weiterleitung zur Zahlung...', key: 'activateSub', duration: 0 });
        try {
            console.log(`Starte Zahlungs-Checkout für inaktiven Shop ${shop.id}, Plan: ${shop.subscriptionPlan}, Intervall: ${shop.subscriptionInterval}`);

            const stripe = await stripePromise;
            if (!stripe) {
                throw new Error('Stripe konnte nicht initialisiert werden.');
            }

            const response = await api.post('/payments/create-checkout-session', {
                shopId: shop.id,
                plan: shop.subscriptionPlan,
                interval: shop.subscriptionInterval,
            });

            if (!response || !response.success || !response.sessionId) {
                console.error("Fehler bei Antwort von create-checkout-session für Aktivierung:", response);
                throw new Error(response.message || 'Checkout-Sitzung konnte nicht erstellt werden oder sessionId fehlt.');
            }

            const { error: stripeError } = await stripe.redirectToCheckout({
                sessionId: response.sessionId,
            });

            if (stripeError) {
                console.error('Stripe Weiterleitungsfehler bei Aktivierung:', stripeError);
                throw stripeError;
            }
            message.destroy('activateSub');
        } catch (error) {
            message.destroy('activateSub');
            console.error("Fehler beim Aktivieren des Abonnements:", error);
            message.error(`Fehler beim Starten der Zahlung: ${error.message || 'Unbekannter Fehler'}`);
        }
    };

    // --- Handle Profile Update ---
    const handleUpdateProfile = async (values) => {
        setIsEditingProfile(true);
        message.loading({ content: 'Profil wird aktualisiert...', key: 'updateProfile' });
        const originalEmail = user?.email; // Alte E-Mail speichern für Vergleich
        try {
            // API-Endpunkt /users/me erwartet { name, email }
            const response = await api.put('/users/me', { name: values.name, email: values.email });

            if (response.success && response.data) { // Prüfe auf response.data
                 const updatedUser = response.data; // Nutze die Daten vom Backend
                 const emailChanged = originalEmail !== updatedUser.email;

                // Erfolgsmeldung zuerst
                message.success({ content: 'Profil erfolgreich aktualisiert.', key: 'updateProfile', duration: 3 });

                // Zusätzliche Meldung, wenn E-Mail geändert wurde
                if (emailChanged) {
                     message.info({
                        content: 'Bitte überprüfen Sie Ihr neues E-Mail-Postfach, um Ihre Adresse zu bestätigen.',
                        key: 'emailVerificationInfo',
                         duration: 7 // Längere Dauer für diese Info
                    });
                }

                setUser(updatedUser); // Aktualisiere den globalen Benutzerstatus

                // Setze die Formularfelder explizit auf die neuen Werte
                editProfileForm.setFieldsValue({
                    name: updatedUser.name,
                    email: updatedUser.email
                });
                // NICHT resetFields() verwenden, da dies die alten Initialwerte wiederherstellt
            } else {
                throw new Error(response.message || 'Profilaktualisierung fehlgeschlagen.');
            }
        } catch (error) {
            console.error("Fehler beim Aktualisieren des Profils:", error);
            // Prüfe auf spezifische Backend-Fehlermeldungen (z.B. E-Mail bereits vergeben)
            let errorMessage = 'Profilaktualisierung fehlgeschlagen.';
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }
            message.error({ content: `Fehler: ${errorMessage}`, key: 'updateProfile', duration: 5 });
        } finally {
            setIsEditingProfile(false);
        }
    };

    // --- Handle Account Deletion ---
    const handleDeleteAccount = async () => {
        setIsDeletingAccount(true);
        message.loading({ content: 'Konto wird gelöscht...', key: 'deleteAccount' });
        try {
            // Annahme: API-Endpunkt /users/me zum Löschen des eigenen Kontos
            const response = await api.delete('/users/me');

            if (response.success) {
                message.success({ content: 'Konto erfolgreich gelöscht. Sie werden abgemeldet.', key: 'deleteAccount', duration: 4 });
                // Nach erfolgreicher Löschung ausloggen
                await logout();
                navigate('/login'); // Weiterleitung zur Login-Seite
            } else {
                throw new Error(response.message || 'Kontolöschung fehlgeschlagen.');
            }
        } catch (error) {
            console.error("Fehler beim Löschen des Kontos:", error);
            const errorMessage = error.response?.data?.message || error.message || 'Kontolöschung fehlgeschlagen.';
            message.error({ content: `Fehler: ${errorMessage}`, key: 'deleteAccount', duration: 5 });
            setIsDeletingAccount(false); // Ladezustand nur bei Fehler zurücksetzen
        }
        // finally wird nicht benötigt, da bei Erfolg navigiert wird
    };

    // --- Confirm Account Deletion ---
    const confirmDeleteAccount = () => {
        Modal.confirm({
            title: 'Möchten Sie Ihr Konto wirklich löschen?',
            icon: <ExclamationCircleOutlined />,
            content: 'Dieser Vorgang kann nicht rückgängig gemacht werden. Alle Ihre Daten, einschließlich Shops und Abonnements, werden dauerhaft entfernt.',
            okText: 'Konto löschen',
            okType: 'danger',
            cancelText: 'Abbrechen',
            onOk: handleDeleteAccount,
            confirmLoading: isDeletingAccount, // Ladeanzeige im Bestätigungsbutton
        });
    };

    // *** NEUER Handler für Seitenwechsel ***
    const handlePageChange = (page, pageSize) => {
        if (user) {
            // Wichtig: pageSize nicht ändern, wenn nicht unterstützt vom Backend/UI
            loadShopData(user, page, pagination.limit);
        }
    };

    // NEUER Handler, um ausstehende Zahlung für bereits gewählten Plan abzuschließen
    const handleCompletePendingPayment = async (shop) => {
        console.log(`Dashboard: Versuche ausstehende Zahlung für Shop ${shop.id} abzuschließen (Plan: ${shop.subscriptionPlan}, Intervall: ${shop.subscriptionInterval})`);
        if (!shop || !shop.id || !shop.subscriptionPlan || !shop.subscriptionInterval) {
            message.error("Fehlende Shop-Daten, um die Zahlung zu starten.");
            return;
        }

        const key = `completePayment-${shop.id}`;
        message.loading({ content: 'Bereite Zahlung vor...', key: key, duration: 0 });

        try {
            const stripe = await stripePromise;
            if (!stripe) {
                throw new Error('Stripe konnte nicht initialisiert werden.');
            }

            // Rufe den Endpunkt zum Erstellen der Checkout Session auf
            const response = await api.post('/payments/create-checkout-session', {
                shopId: shop.id,
                plan: shop.subscriptionPlan,
                interval: shop.subscriptionInterval,
            });

            if (!response || !response.success || !response.sessionId) {
                throw new Error(response.message || 'Checkout-Sitzung konnte nicht erstellt werden.');
            }

            // Leite zu Stripe weiter
            const { error: stripeError } = await stripe.redirectToCheckout({
                sessionId: response.sessionId,
            });

            if (stripeError) {
                throw stripeError; // Stripe-Fehler werfen
            }
            // Nachricht wird bei Erfolg nicht zerstört, da Seite wechselt

        } catch (error) {
            message.destroy(key);
            console.error("Fehler beim Abschließen der ausstehenden Zahlung:", error);
            message.error(`Fehler beim Starten der Zahlung: ${error.message || 'Unbekannter Fehler'}`);
        }
    };

    const renderTabContent = (key) => {
        const contentStyle = {
             padding: '1.5rem'
        };

        switch (key) {
            case 'shops':
                if (loading) {
                    return <div style={{ textAlign: 'center', padding: '50px' }}><Spin size="large" tip="Lade Shops..." /></div>;
                }
                if (error) {
                    return <Alert message="Fehler" description={error} type="error" showIcon closable onClose={() => setError(null)} />;
                }
                return (
                    <>
                        <Row justify="space-between" align="middle" style={{ marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '1px solid #eee' }}>
                             <Col>
                                 <Title level={3} style={{ marginBottom: 0 }}>Meine Shops</Title>
                             </Col>
                             <Col>
                                 <Button
                                     type="primary"
                                     icon={<PlusOutlined />}
                                     onClick={() => navigate('/register')}
                                 >
                                     Neuen Shop hinzufügen
                                 </Button>
                              </Col>
                         </Row>
                        {shops.length === 0 ? (
                            <Empty description="Sie haben noch keine Shops hinzugefügt.">
                                <Button 
                                    type="primary" 
                                    icon={<PlusOutlined />} 
                                    onClick={() => navigate('/register')}
                                >
                                    Jetzt Shop hinzufügen
                                </Button>
                            </Empty>
                        ) : (
                            <Spin spinning={loadingPage} tip="Lade Seite...">
                                <Row gutter={[16, 16]}>
                                    {shops.map(shop => {
                                        const { isActive, subscriptionStatus, subscriptionPlan, subscriptionEnd, id: shopId, slug, name } = shop;
                                        
                                        const problematicStatus = ['pending', 'past_due', 'unpaid', 'incomplete', 'cancelled', 'inactive'];
                                        const needsAttention = !isActive || problematicStatus.includes(subscriptionStatus);
                                        const actionText = getShopActionText(subscriptionStatus, isActive);
                                        const actionType = getShopActionType(subscriptionStatus, isActive);

                                        const currentPlanIndex = plans.findIndex(p => p.id === subscriptionPlan);
                                        const canUpgrade = isActive && currentPlanIndex !== -1 && currentPlanIndex < plans.length - 1;

                                        const isCurrentShopDeleting = isDeletingShop && shopBeingDeleted === shopId; // Prüfen, ob DIESER Shop gelöscht wird

                                        const cardActions = [
                                            <Tooltip title="Shop Details ansehen">
                                                <Button type="link" icon={<EyeOutlined />} onClick={() => navigate(`/shops/${shopId}`)} />
                                            </Tooltip>,
                                            <Tooltip title="Shop bearbeiten">
                                                <Button type="link" icon={<EditOutlined />} onClick={() => navigate(`/dashboard/edit-shop/${shopId}`)} />
                                            </Tooltip>,
                                            // Nur Admins sehen den Löschbutton
                                            ...(user?.role === 'admin' ? [
                                                <Popconfirm
                                                    title={`Sicher, dass Sie den Shop "${name}" löschen möchten? Dies kann nicht rückgängig gemacht werden.`}
                                                    onConfirm={() => handleDeleteShop(shopId)} // Hier die neue Funktion aufrufen
                                                    okText="Ja, löschen"
                                                    cancelText="Abbrechen"
                                                    okButtonProps={{ loading: isCurrentShopDeleting, danger: true }} // Ladezustand und Gefahr
                                                    disabled={isCurrentShopDeleting} // Deaktivieren während Löschvorgang
                                                >
                                                    <Tooltip title="Shop löschen (Admin)">
                                                        {/* Button wird im Popconfirm gerendert */}
                                                        <Button
                                                            type="link"
                                                            danger
                                                            icon={<DeleteOutlined />}
                                                            loading={isCurrentShopDeleting} // Ladezustand auch hier anzeigen
                                                            disabled={isDeletingShop && !isCurrentShopDeleting} // Andere Löschbuttons deaktivieren
                                                        />
                                                    </Tooltip>
                                                </Popconfirm>
                                            ] : [])
                                        ];

                                        return (
                                            <Col key={shopId} xs={24} sm={12} md={12} lg={8}>
                                                <Card
                                                    title={name || 'Unbenannter Shop'}
                                                    bordered={false}
                                                    style={{ height: '100%', display: 'flex', flexDirection: 'column', boxShadow: 'var(--box-shadow)', opacity: isActive ? 1 : 0.6 }}
                                                    bodyStyle={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}
                                                    extra={<Tag color={getPlanTagColor(subscriptionPlan)}>{subscriptionPlan || 'N/A'}</Tag>}
                                                    actions={user?.role === 'admin' ? cardActions : undefined}
                                                >
                                                    <div>
                                                        {needsAttention && actionText && (
                                                            <Alert
                                                                message="Aktion erforderlich"
                                                                description={actionText}
                                                                type={actionType}
                                                                showIcon
                                                                action={
                                                                    <Button 
                                                                        size="small" 
                                                                        type="primary" 
                                                                        onClick={() => {
                                                                            if (subscriptionStatus === 'pending') {
                                                                                // Spezifischer Handler für ausstehende Zahlung
                                                                                handleCompletePendingPayment(shop); 
                                                                            } else {
                                                                                // Für andere Fälle (Tarif wählen/ändern)
                                                                                navigate(`/dashboard/change-plan/${shopId}`);
                                                                            }
                                                                        }}
                                                                        icon={<CreditCardOutlined />}
                                                                    >
                                                                        {subscriptionStatus === 'pending' ? 'Zahlung abschließen' : 'Tarif wählen/ändern'}
                                                                    </Button>
                                                                }
                                                                style={{ marginBottom: '16px' }}
                                                            />
                                                        )}
                                                        <Descriptions size="small" column={1} layout="horizontal">
                                                            <Descriptions.Item label="Status">
                                                                <Tag 
                                                                    color={isActive ? 'success' : 'error'} 
                                                                    icon={isActive ? <CheckCircleOutlined /> : <StopOutlined />}
                                                                >
                                                                    {isActive ? 'Aktiv' : 'Inaktiv'}
                                                                </Tag>
                                                            </Descriptions.Item>
                                                            <Descriptions.Item label="Akt. Tarif">
                                                                {subscriptionPlan ? (
                                                                    <Tag color={getPlanTagColor(subscriptionPlan)}>{subscriptionPlan}</Tag>
                                                                ) : 'Kein Tarif'}
                                                            </Descriptions.Item>
                                                            <Descriptions.Item label="Abo-Status">
                                                                 <Tag color={getSubscriptionTagColor(subscriptionStatus)}>{getSubscriptionStatusText(subscriptionStatus)}</Tag>
                                                            </Descriptions.Item>
                                                            {subscriptionEnd && (
                                                            <Descriptions.Item label="Gültig bis">
                                                               {formatDate(subscriptionEnd)}
                                                            </Descriptions.Item>
                                                            )}
                                                        </Descriptions>
                                                        <Divider dashed style={{margin: '12px 0'}} />
                                                    </div>
                                                    <Space wrap direction={screens.xs ? 'vertical' : 'horizontal'} style={{ width: '100%', justifyContent: 'space-between', marginTop: 'auto' }}>
                                                        <Space wrap>
                                                            <Tooltip title={!isActive ? "Shop muss aktiv sein" : "Shop bearbeiten"}>
                                                                <Button
                                                                    icon={<EditOutlined />}
                                                                    onClick={() => navigate(`/dashboard/edit-shop/${shopId}`)}
                                                                    disabled={!isActive}
                                                                >
                                                                    Bearbeiten
                                                                </Button>
                                                            </Tooltip>
                                                            <Tooltip title={!isActive ? "Shop muss aktiv sein" : "Statistiken ansehen"}>
                                                                 <Button 
                                                                    icon={<BarChartOutlined />} 
                                                                    onClick={() => {
                                                                        setSelectedShopIdForStats(shopId);
                                                                        setActiveTabKey('statistics');
                                                                    }}
                                                                    disabled={!isActive}
                                                                >
                                                                    Statistik
                                                                </Button>
                                                            </Tooltip>
                                                            <Tooltip title="Shop ansehen">
                                                                <Button icon={<EyeOutlined />} onClick={() => window.open(`/shops/${shopId}`, '_blank')}>
                                                                    Vorschau
                                                                </Button>
                                                            </Tooltip>
                                                        </Space>
                                                        <Space wrap>
                                                            {subscriptionStatus !== 'pending' && (
                                                                 <Button 
                                                                    icon={<CreditCardOutlined />} 
                                                                    onClick={() => navigate(`/dashboard/change-plan/${shop.id}`)} 
                                                                    type={canUpgrade ? "primary" : "default"}
                                                                >
                                                                    {canUpgrade ? "Upgrade" : "Tarif ändern"}
                                                                </Button>
                                                            )}
                                                            <Tooltip title={!isActive ? "Shop muss aktiv sein, um gelöscht zu werden" : "Shop endgültig löschen"}>
                                                               <Popconfirm
                                                                    title="Shop wirklich löschen?"
                                                                    description="Dies kann nicht rückgängig gemacht werden."
                                                                    onConfirm={() => confirmDeleteShop(shop)}
                                                                    okText="Ja, löschen"
                                                                    cancelText="Abbrechen"
                                                                    disabled={!isActive}
                                                                >
                                                                    <Button icon={<DeleteOutlined />} danger disabled={!isActive}>
                                                                       Löschen
                                                                    </Button>
                                                                </Popconfirm>
                                                            </Tooltip>
                                                        </Space>
                                                    </Space>
                                                </Card>
                                            </Col>
                                        );
                                    })}
                                </Row>
                                {/* *** Paginierungs-Komponente hinzufügen *** */}
                                {pagination.totalItems > pagination.limit && (
                                     <Pagination
                                        current={pagination.currentPage}
                                        total={pagination.totalItems}
                                        pageSize={pagination.limit}
                                        onChange={handlePageChange}
                                        style={{ textAlign: 'center', marginTop: '20px' }}
                                        showSizeChanger={false} // Deaktivieren, wenn Backend feste Limits hat
                                    />
                                )}
                            </Spin>
                        )}
                    </>
                );
            case 'statistics':
                const selectedShop = shops.find(shop => shop.id === selectedShopIdForStats);

                return (
                    <div style={contentStyle}>
                        <Row justify="space-between" align="middle" style={{ marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '1px solid #eee' }}>
                            <Col>
                                <Title level={3} style={{ marginBottom: 0 }}>Shop-Statistik</Title>
                                <Text type="secondary">Leistung des ausgewählten Shops.</Text>
                            </Col>
                            <Col>
                                {shops && shops.length > 0 ? (
                                    <Select
                                        style={{ width: 250 }}
                                        placeholder="Shop auswählen..."
                                        value={selectedShopIdForStats}
                                        onChange={(value) => setSelectedShopIdForStats(value)}
                                        loading={loading}
                                        disabled={loading}
                                    >
                                        {shops.map(shop => (
                                            <Option key={shop.id} value={shop.id}>{shop.name}</Option>
                                        ))}
                                    </Select>
                                ) : (
                                     !loading && <Text type="secondary">Keine Shops vorhanden.</Text>
                                 )}
                             </Col>
                        </Row>

                        {/* Hinweis zur Datenherkunft (aktualisiert) */}
                        <Alert
                            message="Hinweis zur Datenherkunft"
                            description="Alle angezeigten Daten (Profilaufrufe, Website-Klicks, Bewertungen) sind echt und werden für den ausgewählten Shop gezählt."
                            type="info"
                            showIcon
                            icon={<InfoCircleOutlined />}
                            style={{ marginBottom: '24px' }}
                        />

                        {/* Lade-/Fehler-/Kein Shop ausgewählt Zustand */}   
                        {!selectedShopIdForStats && !loading && shops.length > 0 && (
                            <Empty description="Bitte wählen Sie einen Shop aus, um Statistiken anzuzeigen." />
                        )}
                        
                        {selectedShopIdForStats && (
                            loadingCurrentShopStats ? (
                                <Row justify="center" align="middle" style={{ minHeight: '200px' }}>
                                    <Spin tip={`Lade Statistiken für ${selectedShop?.name || 'Shop'}...`} size="large" />
                                </Row>
                            ) : currentShopStatsError ? (
                                <Alert
                                    message="Fehler beim Laden der Statistik"
                                    description={currentShopStatsError}
                                    type="error"
                                    showIcon
                                    closable
                                />
                            ) : currentShopStats ? (
                                // Statistik-Anzeige
                                <Card bordered={false} style={{ boxShadow: 'var(--box-shadow)' }}>
                                     <Title level={4} style={{ marginTop: 0, marginBottom: '20px' }}>Leistungsdaten für "{selectedShop?.name || 'Unbekannter Shop'}"</Title>
                                    {/* ShopStatistics Komponente mit den Daten des ausgewählten Shops */}
                                    <ShopStatistics aggregatedStats={currentShopStats} />
                                </Card>
                            ) : (
                                // Fall: Laden fertig, kein Fehler, aber keine Daten (sollte nicht passieren bei Erfolg, aber zur Sicherheit)
                                <Empty description={`Für ${selectedShop?.name || 'diesen Shop'} konnten keine Statistikdaten gefunden werden.`} />
                            )
                        )}
                    </div>
                );
            case 'subscription':
                return (
                     <div style={contentStyle}>
                         <Row justify="space-between" align="middle" style={{ marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '1px solid #eee' }}>
                            <Col>
                                 <Title level={3} style={{ marginBottom: 0 }}>Abonnements verwalten</Title>
                            </Col>
                        </Row>
                        {shops.length === 0 && !loading && (
                            <Empty description="Sie haben keine Shops, für die Abonnements verwaltet werden könnten." />
                        )}
                        {loading && <Spin tip="Lade Abonnements..."><div style={{ height: 100 }}></div></Spin>}
                        <Row gutter={[16, 24]}>
                            {shops.map(shop => (
                                <Col key={shop.id} xs={24} md={12} lg={8}>
                                    <Card title={shop.name} bordered={false} style={{ boxShadow: 'var(--box-shadow)' }}>
                                        <p>
                                             <strong>Aktueller Plan:</strong>
                                             <Tag color={getPlanTagColor(shop.subscriptionPlan)} style={{ marginLeft: '8px', fontSize: '0.9em' }}>
                                                 {shop.subscriptionPlan?.charAt(0).toUpperCase() + shop.subscriptionPlan?.slice(1) || 'N/A'}
                                             </Tag>
                                        </p>
                                        <p><strong>Status:</strong> <Tag color={getSubscriptionTagColor(shop.subscriptionStatus)}>{getSubscriptionStatusText(shop.subscriptionStatus || 'inactive')}</Tag></p>
                                        {shop.subscriptionEnd && (
                                            <p><strong>Nächste Zahlung / Ende am:</strong> {formatDate(shop.subscriptionEnd)}</p>
                                        )}

                                        {/* Upgrade Hinweis - Keep existing logic */}
                                        {shop.subscriptionStatus === 'active' && plans && plans.length > 0 && (() => {
                                            const nextPlan = getNextPlan(plans, shop.subscriptionPlan);
                                            if (nextPlan) {
                                                const mainFeatures = nextPlan.features.slice(0, 2).join(', ');
                                                return (
                                                    <div style={{ marginTop: '10px', padding: '10px', background: '#f0faff', border: '1px solid #e6f7ff', borderRadius: '4px' }}>
                                                        <Text strong>
                                                             Upgrade auf {nextPlan.name}?
                                                        </Text>
                                                        <Text style={{ display: 'block', fontSize: '0.9em', color: '#555' }}>
                                                             Profitieren Sie von: {mainFeatures}...
                                                        </Text>
                                                    </div>
                                                );
                                            }
                                            return null;
                                        })()}

                                        <Space direction="vertical" style={{ marginTop: '15px', width: '100%' }}>
                                            {/* Tarif ändern/wählen Button (immer sichtbar wenn nicht geladen wird) */}
                                            <Button
                                                 type="primary"
                                                 onClick={() => navigate(`/dashboard/change-plan/${shop.id}`)}
                                            >
                                                {shop.subscriptionStatus === 'active' ? 'Upgrade/Tarif ändern' : 'Tarif wählen'}
                                            </Button>
                                            {/* Kündigen Button: Nur wenn aktiv UND NICHT basic */}
                                            {shop.subscriptionPlan !== 'basic' && shop.subscriptionStatus === 'active' && (
                                                <Button
                                                     // type="primary" // Consider using danger or default for cancellation
                                                     onClick={() => handleCancelSubscription(shop)}
                                                     danger // Make cancellation button red
                                                >
                                                    Abonnement kündigen
                                                </Button>
                                            )}
                                        </Space>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </div>
                );
            case 'settings':
                return (
                    <div style={contentStyle}>
                        {/* --- Profil bearbeiten --- */}
                        <Row justify="space-between" align="middle" style={{ marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '1px solid #eee' }}>
                            <Col>
                                <Title level={3} style={{ marginBottom: 0 }}>Profil bearbeiten</Title>
                            </Col>
                        </Row>
                        <Form
                            form={editProfileForm}
                            onFinish={handleUpdateProfile}
                            layout="vertical"
                            initialValues={{ name: user?.name, email: user?.email }} // Set initial values
                            style={{ marginBottom: '2.5rem' }} // Add margin below profile form
                        >
                            <Row gutter={[16, 16]}>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Name"
                                        name="name"
                                        rules={[{ required: true, message: 'Bitte geben Sie Ihren Namen ein' }]}
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="E-Mail"
                                        name="email"
                                        rules={[{ required: true, type: 'email', message: 'Bitte geben Sie eine gültige E-Mail ein' }]}
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col xs={24}>
                                    <Form.Item>
                                        <Button type="primary" htmlType="submit" loading={isEditingProfile}>
                                            Profil aktualisieren
                                        </Button>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Form>

                        <Divider /> 

                        {/* --- Passwort ändern --- */}
                         <Row justify="space-between" align="middle" style={{ marginTop: '1.5rem', marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '1px solid #eee' }}>
                            <Col>
                                <Title level={3} style={{ marginBottom: 0 }}>Passwort ändern</Title>
                            </Col>
                        </Row>
                        <Form
                            form={changePasswordForm}
                            onFinish={handleChangePassword}
                            layout="vertical"
                             style={{ marginBottom: '2.5rem' }} // Add margin below password form
                        >
                            <Row gutter={[16, 16]}>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Aktuelles Passwort"
                                        name="currentPassword"
                                        rules={[{ required: true, message: 'Bitte geben Sie Ihr aktuelles Passwort ein' }]}
                                    >
                                        <Input.Password />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Neues Passwort"
                                        name="newPassword"
                                        rules={[
                                            { required: true, message: 'Bitte geben Sie Ihr neues Passwort ein' },
                                            { min: 8, message: 'Passwort muss mindestens 8 Zeichen lang sein'}
                                        ]}
                                    >
                                        <Input.Password />
                                    </Form.Item>
                                </Col>
                                <Col xs={24}>
                                    <Form.Item>
                                        <Button type="primary" htmlType="submit" loading={isChangingPassword}>
                                            Passwort ändern
                                        </Button>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Form>

                        <Divider />

                        {/* --- Konto löschen --- */}
                         <Row justify="space-between" align="middle" style={{ marginTop: '1.5rem', marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '1px solid #eee' }}>
                            <Col>
                                <Title level={3} style={{ marginBottom: 0 }}>Konto löschen</Title>
                            </Col>
                        </Row>
                         <Paragraph type="danger">
                             Achtung: Wenn Sie Ihr Konto löschen, werden alle Ihre Daten, einschließlich Ihrer Shops und Abonnements, dauerhaft entfernt. Dieser Vorgang kann nicht rückgängig gemacht werden.
                         </Paragraph>
                         <Button
                             type="danger"
                             onClick={confirmDeleteAccount}
                             loading={isDeletingAccount}
                             icon={<DeleteOutlined />}
                         >
                             Mein Konto endgültig löschen
                         </Button>
                    </div>
                );
            default:
                return <div>Tab not implemented</div>;
        }
    };

    // Haupt-Layout Struktur
    const sidebarWidth = screens.lg ? 250 : (screens.md ? 200 : 80); // Responsive Sidebarbreite

    return (
        <AntLayout className={`dashboard-page ${screens.xs ? 'mobile' : 'desktop'}`}>
             <Helmet>
                 <title>Dashboard | Snus Verzeichnis Deutschland</title>
                 <meta name="description" content="Verwalten Sie Ihre Snus Shops und Ihr Benutzerkonto."/>
                 <meta name="robots" content="noindex, nofollow" />
             </Helmet>

            <Sider
                collapsible
                // collapsed={!screens.md} // Entfernt, um Kollision mit breakpoint zu vermeiden
                breakpoint="md" // Einklappen unterhalb Medium
                collapsedWidth={screens.xs ? 0 : 80} // Komplett ausblenden auf XS
                style={{
                    background: '#001529', // Dunkler Sidebar-Hintergrund
                }}
                trigger={null} // Wenn manueller Trigger gewünscht
            >
                <AntMenu
                   theme="dark"
                   mode="inline"
                   selectedKeys={[activeTabKey]}
                   onClick={({ key }) => handleTabChange(key)}
                   items={[
                        { key: 'shops', icon: <ShopOutlined />, label: 'Meine Shops' },
                        { key: 'statistics', icon: <BarChartOutlined />, label: 'Statistiken', disabled: shops.length === 0 }, // Deaktivieren, wenn keine Shops
                        { key: 'subscription', icon: <CreditCardOutlined />, label: 'Abonnements', disabled: shops.length === 0 }, // Deaktivieren, wenn keine Shops
                        { key: 'settings', icon: <SettingOutlined />, label: 'Account Settings' },
                        { key: 'logout', icon: <LogoutOutlined />, label: 'Abmelden', onClick: handleLogout }, // Abmelden als Menüpunkt
                   ]}
               />
            </Sider>

            <AntLayout className="ant-layout">
                {/* Optional: Header für zusätzliche Infos/Aktionen */}
                <Header style={{
                    background: '#fff',
                    padding: '0 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    boxShadow: '0 2px 8px #f0f1f2',
                }}>
                    {/* Links: Aktueller Tab-Titel oder Brotkrümelnavigation */}
                    <div>
                        {/* <Title level={4} style={{ margin: 0 }}>{activeTabKey.charAt(0).toUpperCase() + activeTabKey.slice(1)}</Title> */}
                    </div>
                    {/* Rechts: Benutzerinfo/Aktionen */}
                    <Space>
                       {user && <Text>Willkommen, {user.name || user.email}!</Text>}
                        {/* <Button type="text" icon={<LogoutOutlined />} onClick={handleLogout}>Abmelden</Button> */}
                   </Space>
                </Header>

               <Content style={{ margin: screens.md ? '24px 16px' : '16px 8px', overflow: 'initial' }}>
                    {/* Dynamischer Inhalt basierend auf dem aktiven Tab */}
                    {renderTabContent(activeTabKey)}
                </Content>

                <Footer style={{ textAlign: 'center', background: 'transparent', padding: '12px 50px' }}>
                    SnusFinder ©{new Date().getFullYear()} - Dein Dashboard
                </Footer>
            </AntLayout>

            {/* Modals (außerhalb des Hauptlayouts für besseres z-index Management) */}
            {/* Beispiel: Passwort ändern Modal (wenn als Modal implementiert) */}
            {/* <Modal title="Passwort ändern" visible={isChangePasswordModalVisible} onCancel={handleCancelChangePassword} footer={null}> ... </Modal> */}
            {/* Beispiel: Profil bearbeiten Modal */}
            {/* <Modal title="Profil bearbeiten" visible={isEditProfileModalVisible} onCancel={handleCancelEditProfile} footer={null}> ... </Modal> */}

        </AntLayout>
    );
};

export default DashboardPage;